// Chat (HTTP) transport field group for the interface editor drawer
// (agent.md: `*InterfaceFields` split out of AgentConnections.tsx to hold the
// 800-line hard cap — a seam, not a decision change).
import type { components } from "../../../api/schema";
import { HelpTip, Select, Stack, TextInput, Textarea } from "../../../ui";
import type { InterfaceFieldsProps } from "../lib/connectionDraft";

type HttpAuthType = components["schemas"]["HttpAuthType"];

export function HttpInterfaceFields({
  draft,
  onDraftChange,
}: InterfaceFieldsProps) {
  return (
    <Stack gap={4}>
      <TextInput
        label="Endpoint URL"
        required
        type="url"
        value={draft.httpUrl}
        onChange={(event) => {
          onDraftChange({ ...draft, httpUrl: event.currentTarget.value });
        }}
      />
      <Select
        label="Authentication"
        help={
          <HelpTip label="About HTTP authentication">
            Choose the authentication method required by your endpoint. Bearer
            token and API key authentication use a secret stored in your
            deployment's secret manager.
          </HelpTip>
        }
        required
        value={draft.httpAuthType}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            httpAuthType: event.currentTarget.value as HttpAuthType,
          });
        }}
      >
        <option value="none">No authentication</option>
        <option value="bearer">Bearer token</option>
        <option value="api_key">API key</option>
      </Select>
      <TextInput
        label="Secret reference"
        help={
          <HelpTip label="About the authentication secret">
            Enter the reference supplied by your deployment administrator, not
            the secret itself. To replace the secret value, update it in your
            deployment's secret manager. This field changes which stored secret
            the interface uses.
          </HelpTip>
        }
        value={draft.httpSecretRef}
        onChange={(event) => {
          onDraftChange({ ...draft, httpSecretRef: event.currentTarget.value });
        }}
      />
      <Textarea
        help={
          <HelpTip label="About HTTP headers">
            Enter a JSON object of non-secret headers. Configure authentication
            using the secret reference; do not paste tokens or API keys here.
          </HelpTip>
        }
        label="Headers"
        rows={5}
        value={draft.headers}
        onChange={(event) => {
          onDraftChange({ ...draft, headers: event.currentTarget.value });
        }}
      />
    </Stack>
  );
}
